/// Level + map asset loader.
///
/// Loads from `/data/Levels/<filename>` plus the level's referenced
/// basemap, rivers, and (optional) buildings GeoJSON files. Each
/// level entry in the registry maps a stable id (matching the iOS
/// LevelData.id) to a JSON filename + an English display name.

import type { LevelData } from '../types/LevelData'
import type { FeatureCollection } from '../types/GeoJSON'
import { GENERATED_LEVEL_REGISTRY } from './levelRegistry.generated'

export interface LevelBundle {
  level: LevelData
  basemap: FeatureCollection
  rivers: FeatureCollection | null
  buildings: FeatureCollection | null
  contours: FeatureCollection | null
  overlay: FeatureCollection | null
}

export interface LevelRegistryEntry {
  id: string
  displayName: string
  filename: string
  /// One-line description shown in the level-select modal.
  description: string
  /// Coordinates of the first airport's ARP — drives the dot
  /// placement on the world-map home screen.
  pin: { latitude: number; longitude: number }
  /// True ⟹ playable without a purchase. False ⟹ gated behind
  /// the FULL_UNLOCK Stripe IAP. Absent ⟹ treated as free for
  /// back-compat with hand-authored entries.
  isFree?: boolean
}

/// Web-only levels with no iOS counterpart. Hand-authored — not
/// touched by the importer. Extend the array below when adding a
/// new exclusive level (USS Carrier was the first).
const HAND_AUTHORED_LEVELS: readonly LevelRegistryEntry[] = [
  {
    id: 'USSCARRIER',
    displayName: 'USS Carrier',
    filename: 'usscarrier_level.json',
    description:
      'Open-ocean carrier ops — recover and launch fighters from a Nimitz-class deck underway.',
    pin: { latitude: 35.0, longitude: -125.0 },
    isFree: false,
  },
  {
    id: 'HANDOFF_NY',
    displayName: 'Handoff: New York',
    filename: 'handoff_newyork_level.json',
    description: 'Live ADS-B traffic over the New York TRACON — work the real aircraft flying right now.',
    pin: { latitude: 40.7589, longitude: -73.9851 },
    isFree: false,
  },
] as const

/// The set of levels available on the web. The bulk are
/// auto-generated from the iOS source by
/// `scripts/import-ios-levels.mjs`; web-exclusive levels live in
/// `HAND_AUTHORED_LEVELS` and are merged in here.
export const LEVEL_REGISTRY: readonly LevelRegistryEntry[] = [
  ...GENERATED_LEVEL_REGISTRY,
  ...HAND_AUTHORED_LEVELS,
]

export function findLevel(id: string): LevelRegistryEntry | undefined {
  return LEVEL_REGISTRY.find((l) => l.id === id)
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status} ${res.statusText}`)
  return (await res.json()) as T
}

async function fetchOptionalGeo(name: string | null | undefined): Promise<FeatureCollection | null> {
  if (!name) return null
  try {
    return await fetchJson<FeatureCollection>(`/data/${name}.geojson`)
  } catch {
    return null
  }
}

export async function loadLevelById(id: string): Promise<LevelBundle> {
  const entry = findLevel(id)
  if (!entry) throw new Error(`Unknown level id: ${id}`)
  const level = await fetchJson<LevelData>(`/data/Levels/${entry.filename}`)
  // Single global Natural Earth 10m land basemap for every level —
  // the resolution where the basemap matches the rivers files for
  // bays / inlets / capes (50m visibly disagreed at radar zoom).
  // 7.7 MB raw, ~2.2 MB gzipped over the wire, browser-cached after
  // the first level load and reused by every level after. The
  // per-level `mapFileName` field is now ignored in favor of this
  // global basemap.
  const basemap = await fetchJson<FeatureCollection>(`/data/world_land.geojson`)
  const rivers = await fetchOptionalGeo(level.riverFileName)
  const buildings = await fetchOptionalGeo(level.buildingFileName)
  const contours = await fetchOptionalGeo(level.contourFileName)
  const overlay = await fetchOptionalGeo(level.overlayFileName)
  return { level, basemap, rivers, buildings, contours, overlay }
}

/// Backwards-compat shim — old callers that hard-coded the tutorial
/// level can keep working. New code should call `loadLevelById`.
export async function loadTutorialLevel(): Promise<LevelBundle> {
  return loadLevelById('TUTORIAL')
}
